import {
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Group,
  Loader,
  SegmentedControl,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { AppModalHeader, ModalFooter } from "../ui/app-modal";
import { REGISTER_PAYMENT_DRAWER_STYLES } from "../register-payment/register-payment";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSwr from "swr";
import * as InvoiceApi from "../../apis/invoice";
import * as NetworkApi from "../../apis/network";
import { LateFeeApi } from "../../apis/late-fee";
import { ReminderApi } from "../../apis/reminder";
import { useLateFeeConfig } from "../../hooks/use-late-fee-config";
import { currencyFormat } from "../../utils";
import { dateTimeFormat } from "../../utils/date";
import { message } from "../../utils/Message";
import { handleError } from "../../utils/error-handler";
import { renderBusinessName } from "../../utils/render-business-name";
import { renderName } from "../../utils/render-name";
import classes from "./reminder-drawer.module.css";

interface ReminderDrawerProps {
  invoiceId: string;
  opened: boolean;
  onClose: () => void;
  /** Optional customer label when parent already knows it */
  customerName?: string;
}

interface IReminder {
  id: string;
  deliveryMethod: string;
  recipientEmail?: string | null;
  customSubject?: string | null;
  customMessage?: string | null;
  errorMessage?: string | null;
  sentAt?: string | null;
  status: string;
}

type Delivery = "EMAIL" | "SMS" | "IN_APP";

export function ReminderDrawer({ invoiceId, opened, onClose, customerName }: ReminderDrawerProps) {
  const { t } = useTranslation("settings");
  const { lateFeeEnabled } = useLateFeeConfig();

  const [sending, setSending] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState<Delivery>("EMAIL");
  const [useAltEmail, setUseAltEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [includeLateFeeInfo, setIncludeLateFeeInfo] = useState(true);
  const [includePaymentLink, setIncludePaymentLink] = useState(true);

  const { data: invoice } = useSwr(
    opened && invoiceId ? ["swr.reminder.invoice", invoiceId] : null,
    () => InvoiceApi.get(invoiceId),
    { revalidateOnFocus: false },
  );

  const receiverBusinessId = (invoice as any)?.receiverBusinessId as string | undefined;

  const { data: network } = useSwr(
    opened && receiverBusinessId ? ["swr.reminder.network", receiverBusinessId] : null,
    () => NetworkApi.get(receiverBusinessId!),
    { revalidateOnFocus: false },
  );

  const { data: lateFee } = useSwr(
    opened && invoiceId ? ["swr.reminder.late-fees", invoiceId] : null,
    () => LateFeeApi.getInvoiceLateFees(invoiceId),
    { revalidateOnFocus: false },
  );

  const { data: reminderConfig } = useSwr(
    opened ? "swr.reminder-config" : null,
    () => ReminderApi.getConfig(),
    { revalidateOnFocus: false },
  );

  const { data, isLoading, mutate } = useSwr(
    opened && invoiceId ? ["swr.reminders", invoiceId] : null,
    () => ReminderApi.getInvoiceReminders(invoiceId),
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (!opened) return;
    setDeliveryMethod("EMAIL");
    setUseAltEmail(false);
    setRecipientEmail("");
    setCustomSubject("");
    setCustomMessage("");
    setIncludeLateFeeInfo(true);
    setIncludePaymentLink(true);
  }, [opened, invoiceId]);

  const staff = (network as any)?.staff;
  const staffName = staff ? renderName(staff) : t("reminderDrawer.recipientUnknown");
  const staffRole = t("reminderDrawer.financeStaff");
  const staffInitials = staff
    ? [staff.lastName, staff.firstName].filter(Boolean).map((x: string) => x.trim().charAt(0).toUpperCase()).join("") || "-"
    : "-";
  // Staff email only — never partnerEmail (shared across businesses).
  const staffEmail = (staff?.email as string | undefined)?.trim() || "";
  const staffPhone = (staff?.phone as string | undefined)?.trim() || "";
  const networkLoaded = !!network || (!receiverBusinessId && !!invoice);

  useEffect(() => {
    if (!opened || deliveryMethod !== "EMAIL") return;
    // Wait for the invoice *and* the staff contact: before the invoice loads receiverBusinessId is
    // undefined, so the old guard passed with an empty staffEmail and left the alt-email input open.
    if (!networkLoaded) return;
    if (!staffEmail) setUseAltEmail(true);
  }, [opened, deliveryMethod, networkLoaded, staffEmail]);

  const effectiveEmail = useAltEmail ? recipientEmail.trim() : staffEmail;
  const emailReady = deliveryMethod !== "EMAIL" || !!effectiveEmail;
  const canSend = emailReady && !sending;

  const refCode = (data as any)?.refCode || (invoice as any)?.refCode || "-";
  const remaining =
    (lateFee as any)?.remainingAmount ??
    Math.max(0, Number((invoice as any)?.totalAmount || 0) - Number((invoice as any)?.paidAmount || 0));
  const daysPastDue = Number((lateFee as any)?.daysPastDue || 0);
  const buyerLabel =
    customerName ||
    (network ? renderBusinessName(network as any) : "") ||
    t("reminderDrawer.customerFallback");

  const subtitle = `${refCode} · ${buyerLabel} · ${currencyFormat(remaining)} · ${t(
    "reminderDrawer.daysOverdue",
    { count: daysPastDue },
  )}`;

  const defaultSubject = t("reminderDrawer.defaultSubject", {
    refCode,
    days: daysPastDue,
  });

  const preview = useMemo(() => {
    const lines = [
      t("reminderDrawer.previewCustomer", { name: buyerLabel }),
      t("reminderDrawer.previewRef", { refCode }),
      t("reminderDrawer.previewBalance", { amount: currencyFormat(remaining) }),
      t("reminderDrawer.previewDays", { days: daysPastDue }),
    ];
    if (includePaymentLink) lines.push(t("reminderDrawer.previewPaymentLink"));
    if (includeLateFeeInfo && lateFeeEnabled) lines.push(t("reminderDrawer.previewLateFee"));
    if (customMessage.trim()) lines.push(customMessage.trim());
    return lines.join("\n");
  }, [
    t,
    buyerLabel,
    refCode,
    remaining,
    daysPastDue,
    includePaymentLink,
    includeLateFeeInfo,
    lateFeeEnabled,
    customMessage,
  ]);

  const reminders: IReminder[] = (data as any)?.reminders || [];
  const autoOn = !!(reminderConfig as any)?.config?.reminderEnabled;

  const channelLabel =
    deliveryMethod === "EMAIL"
      ? t("reminderDrawer.methodEmail")
      : deliveryMethod === "SMS"
        ? t("reminderDrawer.methodSms")
        : t("reminderDrawer.methodApp");

  const statusLabel = (status: string) => {
    switch (status) {
      case "SENT":
        return t("reminderDrawer.statusDelivered");
      case "FAILED":
        return t("reminderDrawer.statusFailed");
      case "QUEUED":
      case "PENDING":
        return t("reminderDrawer.statusPending");
      default:
        return status;
    }
  };

  const statusColor = (status: string) => {
    if (status === "SENT") return "green";
    if (status === "FAILED") return "red";
    return "gray";
  };

  const handleSend = async (retryReminder?: IReminder) => {
    try {
      setSending(true);
      const method = (retryReminder?.deliveryMethod as Delivery) || deliveryMethod;
      const payload: Record<string, unknown> = {
        deliveryMethod: method,
        customSubject: customSubject.trim() || null,
        customMessage: customMessage.trim() || null,
        includeLateFeeInfo: lateFeeEnabled ? includeLateFeeInfo : false,
        includePaymentLink,
      };
      if (method === "EMAIL") {
        payload.recipientEmail = useAltEmail ? recipientEmail.trim() || null : null;
      }
      const res: any = await ReminderApi.sendNow(invoiceId, payload);
      if (res?.status === "FAILED") {
        message.error(res?.error || t("reminderDrawer.sendError"));
      } else if (res?.status === "SENT") {
        message.success(t("reminderDrawer.sendSuccess"));
      } else {
        // Older backends without status — treat as success only if no throw
        message.success(t("reminderDrawer.sendSuccess"));
      }
      mutate();
    } catch (error) {
      handleError(error, t("reminderDrawer.sendError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      size="md"
      withCloseButton={false}
      styles={REGISTER_PAYMENT_DRAWER_STYLES}
    >
      <Box className={classes.root}>
        <AppModalHeader
          bordered
          title={t("reminderDrawer.title")}
          subtitle={subtitle}
          onClose={onClose}
        />

        {isLoading ? (
          <Flex justify="center" p={40}>
            <Loader />
          </Flex>
        ) : (
          <Box className={classes.body}>
            <Box>
              <Text className={classes.sectionTitle}>{t("reminderDrawer.recipientSection")}</Text>
              <SegmentedControl
                fullWidth
                value={deliveryMethod}
                onChange={(v) => setDeliveryMethod(v as Delivery)}
                data={[
                  { label: t("reminderDrawer.methodEmail"), value: "EMAIL" },
                  { label: t("reminderDrawer.methodSms"), value: "SMS" },
                  { label: t("reminderDrawer.methodApp"), value: "IN_APP" },
                ]}
              />
              <Box className={classes.recipientCard}>
                <Box className={classes.recipientRow}>
                  <Box component="span" className={classes.recipientAvatar} aria-hidden>
                    {staffInitials}
                  </Box>
                  <Box className={classes.recipientText}>
                    <Text className={classes.recipientName}>{staffName}</Text>
                    <Text className={classes.recipientMeta}>
                      {staffRole} · {deliveryMethod === "SMS" ? staffPhone || "-" : staffEmail || "-"}
                    </Text>
                  </Box>
                  {deliveryMethod === "EMAIL" && staffEmail && (
                    <Button variant="default" size="xs" onClick={() => setUseAltEmail((v) => !v)}>
                      {t("reminderDrawer.otherAddress")}
                    </Button>
                  )}
                </Box>
                {deliveryMethod === "EMAIL" && !staffEmail && (
                  <Text size="xs" c="dimmed" mt="xs">
                    {t("reminderDrawer.missingStaffEmail")}
                  </Text>
                )}
                {deliveryMethod === "EMAIL" && useAltEmail && (
                  <TextInput
                    mt="xs"
                    size="sm"
                    type="email"
                    required={!staffEmail}
                    placeholder={t("reminderDrawer.emailPlaceholder")}
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.currentTarget.value)}
                  />
                )}
              </Box>
            </Box>

            <Box>
              <Text className={classes.sectionTitle}>{t("reminderDrawer.messageSection")}</Text>
              <TextInput
                label={t("reminderDrawer.subjectLabel")}
                placeholder={defaultSubject}
                value={customSubject}
                onChange={(e) => setCustomSubject(e.currentTarget.value)}
                mb="sm"
              />
              <Textarea
                label={t("reminderDrawer.noteLabel")}
                placeholder={t("reminderDrawer.notePlaceholder")}
                minRows={3}
                value={customMessage}
                onChange={(e) => setCustomMessage(e.currentTarget.value)}
              />
              <Box className={classes.previewBox}>{preview}</Box>
              <StackChecks
                includePaymentLink={includePaymentLink}
                setIncludePaymentLink={setIncludePaymentLink}
                includeLateFeeInfo={includeLateFeeInfo}
                setIncludeLateFeeInfo={setIncludeLateFeeInfo}
                lateFeeEnabled={lateFeeEnabled}
                t={t}
              />
            </Box>

            <Box>
              <Text className={classes.sectionTitle}>
                {t("reminderDrawer.historySection", { count: reminders.length })}
              </Text>
              {reminders.length === 0 ? (
                <Text className={classes.empty}>{t("reminderDrawer.noReminders")}</Text>
              ) : (
                reminders.map((r) => (
                  <Box key={r.id} className={classes.historyItem}>
                    <Box
                      className={`${classes.dot} ${
                        r.status === "SENT"
                          ? classes.dotSent
                          : r.status === "FAILED"
                            ? classes.dotFailed
                            : classes.dotQueued
                      }`}
                    />
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Group gap="xs" wrap="wrap">
                        <Text size="sm" fw={500}>
                          {t(`reminderDrawer.method${methodKey(r.deliveryMethod)}`)}
                        </Text>
                        <Text size="sm" c="dimmed">
                          · {r.recipientEmail || "-"}
                        </Text>
                        <Tooltip
                          label={r.errorMessage || t("reminderDrawer.statusFailed")}
                          disabled={r.status !== "FAILED" || !r.errorMessage}
                          multiline
                          maw={320}
                        >
                          <Badge size="sm" color={statusColor(r.status)} variant="light">
                            {statusLabel(r.status)}
                          </Badge>
                        </Tooltip>
                      </Group>
                      <Text size="xs" c="dimmed" mt={2}>
                        {r.sentAt ? dateTimeFormat(r.sentAt) : "-"}
                      </Text>
                      {r.status === "FAILED" && (
                        <Anchor
                          component="button"
                          type="button"
                          size="xs"
                          mt={4}
                          onClick={() => handleSend(r)}
                        >
                          {t("reminderDrawer.resend")}
                        </Anchor>
                      )}
                    </Box>
                  </Box>
                ))
              )}
              <Text className={classes.autoRow} mt="sm">
                {t("reminderDrawer.autoReminder", {
                  state: autoOn ? t("reminderDrawer.autoOn") : t("reminderDrawer.autoOff"),
                })}{" "}
                <Anchor component={Link} to="/settings" size="xs">
                  {t("reminderDrawer.settingsLink")}
                </Anchor>
              </Text>
            </Box>
          </Box>
        )}

        <ModalFooter sticky>
          <Text className={classes.footerHint}>{t("reminderDrawer.footerHint")}</Text>
          <Button variant="default" onClick={onClose}>
            {t("reminderDrawer.cancel")}
          </Button>
          <Button loading={sending} disabled={!canSend} onClick={() => handleSend()}>
            {t("reminderDrawer.sendVia", { channel: channelLabel })}
          </Button>
        </ModalFooter>
      </Box>
    </Drawer>
  );
}

function methodKey(method: string): string {
  if (method === "SMS") return "Sms";
  if (method === "IN_APP") return "App";
  return "Email";
}

function StackChecks({
  includePaymentLink,
  setIncludePaymentLink,
  includeLateFeeInfo,
  setIncludeLateFeeInfo,
  lateFeeEnabled,
  t,
}: {
  includePaymentLink: boolean;
  setIncludePaymentLink: (v: boolean) => void;
  includeLateFeeInfo: boolean;
  setIncludeLateFeeInfo: (v: boolean) => void;
  lateFeeEnabled: boolean;
  t: (key: string) => string;
}) {
  return (
    <Box mt="sm">
      <Checkbox
        label={t("reminderDrawer.includePaymentLink")}
        checked={includePaymentLink}
        onChange={(e) => setIncludePaymentLink(e.currentTarget.checked)}
        mb="xs"
      />
      <Checkbox
        label={t("reminderDrawer.includeLateFee")}
        checked={lateFeeEnabled && includeLateFeeInfo}
        disabled={!lateFeeEnabled}
        onChange={(e) => setIncludeLateFeeInfo(e.currentTarget.checked)}
      />
      {!lateFeeEnabled && (
        <Text size="xs" c="dimmed" mt={4}>
          {t("reminderDrawer.lateFeeDisabledHint")}
        </Text>
      )}
    </Box>
  );
}
